import React from 'react';
import { View, Text, TouchableOpacity, ScrollView } from 'react-native';
import { useRouter } from 'expo-router';
import { useThemedStyles } from '../../src/theme/useThemedStyles';
import type { Theme } from '../../src/theme/types';

export default function ResetWarningScreen() {
  const router = useRouter();
  const styles = useThemedStyles(createStyles);

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.scrollContent}
    >
      <View style={styles.card}>
        <View style={styles.iconContainer}>
          <Text style={styles.iconText}>{'\u26A0\uFE0F'}</Text>
        </View>
        <Text style={styles.title}>Reset Your Account</Text>
        <Text style={styles.subtitle}>
          If you've lost your recovery phrase and can't access your encryption
          key from another device, you can reset your account. This is
          irreversible.
        </Text>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>What will be DELETED:</Text>
          <View style={styles.listItem}>
            <Text style={styles.deleteIcon}>{'\u2716'}</Text>
            <Text style={styles.deleteText}>All visions</Text>
          </View>
          <View style={styles.listItem}>
            <Text style={styles.deleteIcon}>{'\u2716'}</Text>
            <Text style={styles.deleteText}>All goals</Text>
          </View>
          <View style={styles.listItem}>
            <Text style={styles.deleteIcon}>{'\u2716'}</Text>
            <Text style={styles.deleteText}>All tasks and recurring schedules</Text>
          </View>
          <View style={styles.listItem}>
            <Text style={styles.deleteIcon}>{'\u2716'}</Text>
            <Text style={styles.deleteText}>All encryption keys</Text>
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>What will be PRESERVED:</Text>
          <View style={styles.listItem}>
            <Text style={styles.keepIcon}>{'\u2714'}</Text>
            <Text style={styles.keepText}>Your account and identity</Text>
          </View>
          <View style={styles.listItem}>
            <Text style={styles.keepIcon}>{'\u2714'}</Text>
            <Text style={styles.keepText}>Contacts and shared connections</Text>
          </View>
          <View style={styles.listItem}>
            <Text style={styles.keepIcon}>{'\u2714'}</Text>
            <Text style={styles.keepText}>App settings and preferences</Text>
          </View>
          <View style={styles.listItem}>
            <Text style={styles.keepIcon}>{'\u2714'}</Text>
            <Text style={styles.keepText}>Your plan</Text>
          </View>
        </View>

        <TouchableOpacity
          style={styles.continueButton}
          onPress={() => router.push('/(setup)/reset-confirm')}
          activeOpacity={0.7}
          accessibilityLabel="Continue with account reset"
          accessibilityHint="Proceeds to the confirmation step"
          accessibilityRole="button"
          testID="reset-warning.continue-button"
        >
          <Text style={styles.continueButtonText}>Continue</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.backButton}
          onPress={() => router.back()}
          activeOpacity={0.7}
          accessibilityLabel="Go back"
          accessibilityRole="button"
          testID="reset-warning.back-button"
        >
          <Text style={styles.backButtonText}>Go Back</Text>
        </TouchableOpacity>
      </View>
    </ScrollView>
  );
}

const createStyles = (theme: Theme) => ({
  container: { flex: 1 as const, backgroundColor: theme.colors.brandedBackground },
  scrollContent: {
    flexGrow: 1 as const,
    justifyContent: 'center' as const,
    paddingHorizontal: 16,
    paddingVertical: 32,
  },
  card: {
    backgroundColor: theme.colors.surface,
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 5,
  },
  iconContainer: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: theme.colors.errorBg,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    alignSelf: 'center' as const,
    marginBottom: 16,
  },
  iconText: { fontSize: 28 },
  title: {
    fontSize: 20,
    fontWeight: '700' as const,
    color: theme.colors.textPrimary,
    textAlign: 'center' as const,
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 14,
    color: theme.colors.textMuted,
    textAlign: 'center' as const,
    marginBottom: 20,
    lineHeight: 20,
  },
  section: {
    backgroundColor: theme.colors.bgSecondary,
    borderRadius: 10,
    padding: 14,
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '700' as const,
    color: theme.colors.textPrimary,
    marginBottom: 10,
  },
  listItem: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    marginBottom: 8,
  },
  deleteIcon: { fontSize: 14, color: theme.colors.error, marginRight: 10, width: 20 },
  deleteText: { fontSize: 14, color: theme.colors.errorText, flex: 1 },
  keepIcon: { fontSize: 14, color: theme.colors.success, marginRight: 10, width: 20 },
  keepText: { fontSize: 14, color: theme.colors.successText, flex: 1 },
  continueButton: {
    backgroundColor: theme.colors.error,
    borderRadius: 10,
    paddingVertical: 14,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    minHeight: 48,
    marginBottom: 10,
  },
  continueButtonText: { color: theme.colors.textOnPrimary, fontSize: 16, fontWeight: '700' as const },
  backButton: {
    borderWidth: 1,
    borderColor: theme.colors.borderMedium,
    borderRadius: 10,
    paddingVertical: 14,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    minHeight: 48,
  },
  backButtonText: { color: theme.colors.textMuted, fontSize: 16, fontWeight: '600' as const },
});
